import { useState } from "react";
import { Link, Outlet, useNavigate, useLocation } from "react-router-dom";
import {
  Home,
  Ticket,
  FolderOpen,
  CreditCard,
  BookOpen,
  Bell,
  LogOut,
  Menu,
  User,
  Users,
  Building2,
  Shield,
  Activity,
  ExternalLink
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { getUserData, logout, getAuthToken } from "@/lib/auth";
import { cn } from "@/lib/utils";
import { useToast } from "@/hooks/use-toast";

const ClientLayout = () => {
  console.log("🟣 ClientLayout RENDERIZADO!");
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [unreadNotifications, setUnreadNotifications] = useState(3); // TODO: Fetch from API
  const navigate = useNavigate();
  const location = useLocation();
  const { toast } = useToast();
  
  const user = getUserData();

  const handleLogout = async () => {
    await logout();
    navigate("/area-cliente");
  };

  const handleAccessPulse = async () => {
    const portalToken = getAuthToken();
    
    if (!portalToken) {
      toast({
        title: 'Erro de Sessão',
        description: 'Sessão expirada. Por favor, faça login novamente.',
        variant: 'destructive'
      });
      return;
    }

    try {
      // 1. Solicita ao Backend do Portal (PHP) que gere um link de acesso seguro
      // Isso evita chamar o backend do Pulse diretamente e expor IPs ou portas
      const response = await fetch('/api/auth/sso_pulse.php', {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${portalToken}` // Envia o token atual para validação
        }
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Falha na autenticação SSO');
      }

      if (data.redirect_url) {
        toast({
          title: 'Acesso Autorizado',
          description: 'Redirecionando para o Coddar Pulse...',
        });
        
        // 2. Abre o Coddar Pulse em nova aba com o token seguro gerado pelo PHP
        window.open(data.redirect_url, '_blank');
      } else {
        throw new Error('URL de redirecionamento não recebida.');
      }

    } catch (error) {
      console.error('Erro SSO:', error);
      toast({
        title: 'Erro de Acesso',
        description: 'Não foi possível acessar o Coddar Pulse. Verifique se você tem permissão de Administrador.',
        variant: 'destructive'
      });
    }
  };

  // Check if user is admin (internal user with admin role)
  const isAdmin = user?.user_type === 'internal' && (user?.role_id === 1 || user?.role_id === 2 || user?.role_id === 4);
  
  const menuItems = [
    { icon: Home, label: "Dashboard", path: "/cliente/dashboard" },
    { icon: Ticket, label: "Tickets", path: "/cliente/tickets" },
    { icon: FolderOpen, label: "Projetos", path: "/cliente/projetos" },
    // Faturas só aparecem para clientes (não para admins)
    ...(!isAdmin ? [{ icon: CreditCard, label: "Faturas", path: "/cliente/faturas" }] : []),
    { icon: BookOpen, label: "Base de Conhecimento", path: "/cliente/conhecimento" },
  ];

  // Admin menu items - only shown to admin users
  const adminMenuItems = isAdmin ? [
    { icon: Ticket, label: "Gerenciar Tickets", path: "/cliente/admin/tickets" },
    { icon: CreditCard, label: "Gerenciar Faturas", path: "/cliente/admin/faturas" },
    { icon: Users, label: "Usuários", path: "/cliente/admin/usuarios" },
    { icon: Building2, label: "Clientes", path: "/cliente/admin/clientes" },
  ] : [];

  const isActivePath = (path: string) => location.pathname === path;

  const NavLinks = ({ mobile = false }: { mobile?: boolean }) => (
    <nav className={cn("flex gap-1", mobile ? "flex-col" : "flex-col")}>
      {menuItems.map((item) => {
        const Icon = item.icon;
        const isActive = isActivePath(item.path);

        return (
          <Link
            key={item.path}
            to={item.path}
            onClick={() => mobile && setIsMobileMenuOpen(false)}
            className={cn(
              "flex items-center gap-3 px-4 py-3 rounded-lg transition-all duration-200",
              isActive
                ? "bg-primary text-primary-foreground shadow-sm"
                : "text-muted-foreground hover:bg-accent hover:text-accent-foreground"
            )}
          >
            <Icon className="h-5 w-5" />
            <span className="font-medium">{item.label}</span>
          </Link>
        );
      })}

      {/* Coddar Pulse - External Link (Apenas Admins vêm isso geralmente, ou todos dependendo da regra de negócio) */}
      {isAdmin && (
        <>
          <div className="my-2 border-t border-border" />
          <button
            onClick={() => {
              handleAccessPulse();
              mobile && setIsMobileMenuOpen(false);
            }}
            className={cn(
              "flex items-center gap-3 px-4 py-3 rounded-lg transition-all duration-200 w-full text-left",
              "bg-gradient-to-r from-primary/10 to-primary/5 border border-primary/20",
              "text-primary hover:from-primary/20 hover:to-primary/10"
            )}
          >
            <Activity className="h-5 w-5" />
            <span className="font-medium flex-1">Coddar Pulse</span>
            <ExternalLink className="h-4 w-4" />
          </button>
        </>
      )}

      {/* Admin Section */}
      {adminMenuItems.length > 0 && (
        <>
          <div className="my-2 border-t border-border" />
          <div className="flex items-center gap-2 px-4 py-2 text-xs font-semibold text-muted-foreground uppercase">
            <Shield className="h-4 w-4" />
            Administração
          </div>
          {adminMenuItems.map((item) => {
            const Icon = item.icon;
            const isActive = isActivePath(item.path);

            return (
              <Link
                key={item.path}
                to={item.path}
                onClick={() => mobile && setIsMobileMenuOpen(false)}
                className={cn(
                  "flex items-center gap-3 px-4 py-3 rounded-lg transition-all duration-200",
                  isActive
                    ? "bg-primary text-primary-foreground shadow-sm"
                    : "text-muted-foreground hover:bg-accent hover:text-accent-foreground"
                )}
              >
                <Icon className="h-5 w-5" />
                <span className="font-medium">{item.label}</span>
              </Link>
            );
          })}
        </>
      )}
    </nav>
  );

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="sticky top-0 z-50 w-full border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
        <div className="container flex h-16 items-center justify-between px-4">
          {/* Logo and Mobile Menu */}
          <div className="flex items-center gap-4">
            <Sheet open={isMobileMenuOpen} onOpenChange={setIsMobileMenuOpen}>
              <SheetTrigger asChild className="lg:hidden">
                <Button variant="ghost" size="icon">
                  <Menu className="h-5 w-5" />
                </Button>
              </SheetTrigger>
              <SheetContent side="left" className="w-64 p-0">
                <div className="flex h-16 items-center border-b px-6">
                  <Link to="/cliente/dashboard" onClick={() => setIsMobileMenuOpen(false)}>
                    <img src="/logo.png" alt="Coddar" className="h-8 w-auto" />
                  </Link>
                </div>
                <div className="p-4">
                  <NavLinks mobile />
                </div>
              </SheetContent>
            </Sheet>

            <Link to="/cliente/dashboard" className="flex items-center">
              <img src="/logo.png" alt="Coddar" className="h-8 w-auto" />
            </Link>
          </div>

          {/* Right Side - Notifications and User Menu */}
          <div className="flex items-center gap-2">
            {/* Notifications */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="icon" className="relative">
                  <Bell className="h-5 w-5" />
                  {unreadNotifications > 0 && (
                    <Badge
                      variant="destructive"
                      className="absolute -top-1 -right-1 h-5 w-5 flex items-center justify-center p-0 text-xs"
                    >
                      {unreadNotifications}
                    </Badge>
                  )}
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-80">
                <DropdownMenuLabel>Notificações</DropdownMenuLabel>
                <DropdownMenuSeparator />
                <div className="max-h-96 overflow-y-auto">
                  {/* TODO: Map actual notifications */}
                  <div className="p-3 hover:bg-accent cursor-pointer">
                    <p className="text-sm font-medium">Ticket #1234 atualizado</p>
                    <p className="text-xs text-muted-foreground mt-1">Há 2 horas</p>
                  </div>
                  <div className="p-3 hover:bg-accent cursor-pointer">
                    <p className="text-sm font-medium">Novo marco concluído no projeto</p>
                    <p className="text-xs text-muted-foreground mt-1">Há 5 horas</p>
                  </div>
                  <div className="p-3 hover:bg-accent cursor-pointer">
                    <p className="text-sm font-medium">Fatura #5678 disponível</p>
                    <p className="text-xs text-muted-foreground mt-1">Ontem</p>
                  </div>
                </div>
                <DropdownMenuSeparator />
                <DropdownMenuItem asChild>
                  <Link to="/cliente/notificacoes" className="w-full cursor-pointer">
                    Ver todas as notificações
                  </Link>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>

            {/* User Menu */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" className="flex items-center gap-2 px-2">
                  <Avatar className="h-8 w-8">
                    <AvatarImage src={user?.avatar_url || undefined} />
                    <AvatarFallback>
                      {user?.full_name?.split(" ").map(n => n[0]).join("").toUpperCase() || "U"}
                    </AvatarFallback>
                  </Avatar>
                  <span className="hidden md:inline-block text-sm font-medium">
                    {user?.full_name || "Usuário"}
                  </span>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuLabel>
                  <div className="flex flex-col space-y-1">
                    <p className="text-sm font-medium">{user?.full_name || "Usuário"}</p>
                    <p className="text-xs text-muted-foreground">{user?.email || ""}</p>
                  </div>
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem asChild>
                  <Link to="/cliente/perfil" className="cursor-pointer">
                    <User className="mr-2 h-4 w-4" />
                    Meu Perfil
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={handleLogout} className="cursor-pointer text-destructive">
                  <LogOut className="mr-2 h-4 w-4" />
                  Sair
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </header>

      <div className="container flex">
        {/* Sidebar - Desktop */}
        <aside className="hidden lg:block w-64 border-r min-h-[calc(100vh-4rem)] p-4">
          <NavLinks />
        </aside>

        {/* Main Content */}
        <main className={cn("flex-1", isAdmin ? "p-6" : "p-6")}>
          <Outlet />
        </main>
      </div>
    </div>
  );
};

export default ClientLayout;